import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Grid, Card, CardContent, LinearProgress, Avatar, Divider, List, ListItem, ListItemAvatar, ListItemText, Button } from '@mui/material';
import { Assignment, CheckCircle, Schedule, Warning, Refresh, EmojiEvents, Speed, AccessTime, Weekend } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { getDashboardStats, getWorkersStats } from '../api';

const StatCard = ({ title, value, IconComponent, color, subtitle }) => {
  const bgColor = color + '1a';
  const borderColor = color + '33';
  
  return (
    <Card sx={{ height: '100%', bgcolor: bgColor, border: '1px solid ' + borderColor, borderRadius: 3, transition: 'all 0.3s ease' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: color }}>
            <IconComponent sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>{value}</Typography>
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>{title}</Typography>
        {subtitle && <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
};

const WorkerCard = ({ name, role, completed, efficiency }) => {
  let progressColor = '#ef4444';
  if (efficiency > 80) progressColor = '#10b981';
  else if (efficiency > 50) progressColor = '#f59e0b';
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
      <Avatar sx={{ width: 44, height: 44, bgcolor: '#3b82f6', fontSize: '1rem', fontWeight: 600 }}>{name.charAt(0)}</Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>{name}</Typography>
        <Typography variant="caption" sx={{ color: '#64748b' }}>{role}</Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>{completed}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LinearProgress variant="determinate" value={efficiency}
            sx={{ width: 50, height: 4, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: progressColor } }} />
          <Typography variant="caption" sx={{ color: '#64748b' }}>{efficiency}%</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const DashboardNew = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalTasks: 0, completedTasks: 0, inProgressTasks: 0, overduesTasks: 0, totalWorkers: 0, totalSofas: 0, deliveredSofas: 0 });
  const [workers, setWorkers] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [sofaModelsData, setSofaModelsData] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => { refreshData(); }, []);

  const refreshData = async () => {
    setLoading(true);
    try {
      const [dashRes, workersRes] = await Promise.all([
        getDashboardStats(),
        getWorkersStats(),
      ]);

      const dash = dashRes.data;
      const taskSummary = dash.task_summary || {};

      setStats({
        totalTasks: taskSummary.total || 0,
        completedTasks: taskSummary.completed || 0,
        inProgressTasks: taskSummary.in_progress || 0,
        overduesTasks: dash.overdue_tasks || 0,
        totalWorkers: dash.total_workers || 0,
        totalSofas: dash.total_sofas || 0,
        deliveredSofas: dash.delivered_sofas || 0,
      });

      // Worker stats
      const wStats = workersRes.data || [];
      const topWorkers = wStats.slice(0, 4).map(w => ({
        name: w.full_name || w.name || 'Работник',
        role: w.role === 'frame_worker' ? 'Каркасник' : w.role === 'sewer' ? 'Швея' : w.role === 'upholsterer' ? 'Обивщик' : 'Мастер',
        completed: w.completed_tasks || 0,
        efficiency: w.efficiency ?? Math.min(100, Math.round((w.completed_tasks || 0) / Math.max(1, (w.total_tasks || 1)) * 100)),
      }));
      setWorkers(topWorkers);

      // Model distribution from dashboard
      const modelColors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
      setSofaModelsData((dash.models_distribution || []).map((m, i) => ({
        name: m.name || m.model, count: m.count, color: modelColors[i % modelColors.length],
      })));

      // Weekly data from dashboard
      setWeeklyData(dash.weekly_stats || []);
      const currentAlerts = [];
      if (dash.overdue_tasks > 0) currentAlerts.push({ id: 'overdue', type: 'warning', message: `Просрочено задач: ${dash.overdue_tasks}`, time: 'Требует внимания' });
      if (dash.low_fabric_alerts > 0) currentAlerts.push({ id: 'fabric', type: 'warning', message: `Материалы с низким остатком: ${dash.low_fabric_alerts}`, time: 'Проверьте склад' });
      if (dash.today_deadlines > 0) currentAlerts.push({ id: 'deadlines', type: 'info', message: `Срок сегодня: ${dash.today_deadlines}`, time: 'Сегодня' });
      setAlerts(currentAlerts);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };
  const completionPercent = stats.totalTasks > 0
    ? Math.round((stats.completedTasks / stats.totalTasks) * 100)
    : 0;

  return (
    <Box sx={{ bgcolor: '#f8fafc' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>Панель управления</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Обзор производства</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', '& .MuiButton-root': { flex: { xs: '1 1 auto', sm: 'none' } } }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={refreshData} sx={{ borderColor: '#e2e8f0', color: '#64748b' }}>Обновить</Button>
          <Button variant="contained" startIcon={<Assignment />} onClick={() => navigate('/plan')} sx={{ bgcolor: '#3b82f6' }}>Новая задача</Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Всего задач" value={stats.totalTasks} IconComponent={Assignment} color="#3b82f6" subtitle="Все производственные задачи" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Выполнено" value={stats.completedTasks} IconComponent={CheckCircle} color="#10b981" subtitle={completionPercent + '% от общего'} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="В работе" value={stats.inProgressTasks} IconComponent={Schedule} color="#f59e0b" subtitle="Активные" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Просрочено" value={stats.overduesTasks} IconComponent={Warning} color="#ef4444" subtitle="Требует внимания" />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>Статистика за неделю</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={weeklyData} barGap={8}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <RechartsTooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: 8, color: 'white' }} />
                <Bar dataKey="completed" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Выполнено" />
                <Bar dataKey="inProgress" fill="#10b981" radius={[4, 4, 0, 0]} name="В работе" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Weekend sx={{ color: '#3b82f6' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Модели диванов</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sofaModelsData.map((model) => (
                <Box key={model.name}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500 }}>{model.name}</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>{model.count} шт.</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={(model.count / Math.max(1, ...sofaModelsData.map(item => item.count))) * 100}
                    sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: model.color } }} />
                </Box>
              ))}
            </Box>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 2, textAlign: 'center' }}>
              Всего в производстве: {sofaModelsData.reduce((sum, m) => sum + m.count, 0)} диванов
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <EmojiEvents sx={{ color: '#f59e0b' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Лучшие работники</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {workers.map((worker, index) => (<WorkerCard key={index} {...worker} />))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Speed sx={{ color: '#3b82f6' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Показатели</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>Выполнено</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>{completionPercent}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={completionPercent} sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#10b981' } }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>В работе</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#3b82f6' }}>{stats.totalTasks ? Math.round(stats.inProgressTasks / stats.totalTasks * 100) : 0}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={stats.totalTasks ? stats.inProgressTasks / stats.totalTasks * 100 : 0} sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#3b82f6' } }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>Без просрочки</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#f59e0b' }}>{stats.totalTasks ? Math.max(0, Math.round((stats.totalTasks - stats.overduesTasks) / stats.totalTasks * 100)) : 0}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={stats.totalTasks ? Math.max(0, (stats.totalTasks - stats.overduesTasks) / stats.totalTasks * 100) : 0} sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#f59e0b' } }} />
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>{stats.totalWorkers}</Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>Работников</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>{stats.totalSofas}</Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>Диванов</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>{stats.deliveredSofas}</Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>Доставлено</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <AccessTime sx={{ color: '#8b5cf6' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Текущие сигналы</Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {alerts.length === 0 && (
                <ListItem sx={{ px: 0 }}><ListItemText primary="Нет сигналов, требующих внимания" primaryTypographyProps={{ variant: 'body2', color: '#64748b' }} /></ListItem>
              )}
              {alerts.map((alert) => {
                let bgColor = '#3b82f620';
                let avatarColor = '#3b82f6';
                let IconComp = Assignment;
                if (alert.type === 'warning') { bgColor = '#f59e0b20'; avatarColor = '#f59e0b'; IconComp = Warning; }
                else if (alert.type === 'success') { bgColor = '#10b98120'; avatarColor = '#10b981'; IconComp = CheckCircle; }
                return (
                  <ListItem key={alert.id} sx={{ px: 2, py: 1.5, mb: 1, borderRadius: 2, bgcolor: bgColor }}>
                    <ListItemAvatar>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: avatarColor }}><IconComp sx={{ fontSize: 18 }} /></Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={alert.message} secondary={alert.time}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500, color: '#1e293b' }}
                      secondaryTypographyProps={{ variant: 'caption', color: '#94a3b8' }} />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardNew;
